import type { EventDetail } from '@/types';

// Must be exactly in sync with the backend API response structure
export interface EventDetailDTO {
  id: number;
  price: number;
  min_price_from: boolean;
  dankort_price: number | null;
  sso_prices: {
    [key: string]: {
      price: number;
      dankort_price: number;
    };
  };
  info1: string;
  info2: string;
  venue?: number;
  publishtime: number; // unix/epoch time in seconds
  salestart: number; // unix/epoch time in seconds
  saleend: number; // unix/epoch time in seconds
  eventtime: number; // unix/epoch time in seconds
  publishtime_gmt_offset: number; // offset in seconds to add to publishtime. Add this OR convert to timezone-aware DateTime using tz_name and extract danish time.
  saleend_gmt_offset: number; // offset in seconds to add to saleend. Add this OR convert to timezone-aware DateTime using tz_name and extract danish time.
  salestart_gmt_offset: number; // offset in seconds to add to salestart. Add this OR convert to timezone-aware DateTime using tz_name and extract danish time.
  eventtime_gmt_offset: number; // offset in seconds to add to eventtime. Add this OR convert to timezone-aware DateTime using tz_name and extract danish time.
  remarks: string;
  description?: string;
  currency?: string;
  photo: string | null;
  ticket_picker_photo: string;
  ticket_picker_photo_location: number;
  event_price_photo: string | null;
  video_url: string;
  special_categories: string[];
  extra_photos: [];
  specialfields: object;
  categories: string[];
  related_events: number[];
  multievent: number | null;
  language: string;
  languages: string[];
  localizations: {
    [key: string]: {
      info1?: string;
      info2?: string;
      remarks?: string;
      description?: string;
      button_text_find_tickets?: string;
      button_text_buy_tickets?: string;
      ticket_info_message_text?: string;
      price_override_text?: string;
      single_sign_on_price_override_text?: string;
    };
  };

  eventtime_override: string;
  single_sign_on_required: number;
  single_sign_on_required_usertypes: [];
  waitlists: [];
  hide_price: boolean;
  tz_name: string;
  closed_event: boolean;
  mailshipping: boolean;
  external_ticketsale_price: number;
  external_ticketsale_url: string;
  external_ticketsale_soldout: boolean;
  parallel_external_ticketsale_primary_message: string;
  parallel_external_ticketsale_secondary_message: string;
  parallel_external_single_sign_on_exclusive_message: string;
  parallel_external_ticketsale_url: string;
  parallel_external_ticketsale_soldout: boolean;
  parallel_external_ticketsale_eventpage_hide_find_tickets_button: boolean;
  status_message_text: string;
  status_message_visible: boolean;
  price_override_active: boolean;
  price_override_text: string;
  single_sign_on_price_override_active: boolean;
  single_sign_on_price_override_text: string;
  ticket_info_message_active: boolean;
  soldout: boolean;
  few_tickets: boolean;
  doorsale_button: boolean;
  doorsale_soldout: boolean;
  invoice_payment_ean: boolean;
  invoice_payment_email: boolean;
  show_place: boolean;
  show_saleends: boolean; // Not in use
  show_remind: boolean;
  custom_boxes: [];
  variant_data: {};
  variant_settings: {
    tiers: [];
  };
  button_text_find_tickets: string;
  button_text_buy_tickets: string;
  button_text_upsale: string;
  saleend_countdown: number;
  show_remaining_tickets: number;
  seatmap_max_distance: number;
  frontpage_event_text_desktop_event_name: boolean;
  frontpage_event_text_desktop_date: boolean;
  frontpage_event_text_desktop_venue: boolean;
  frontpage_event_text_desktop_categories: boolean;
  frontpage_event_text_desktop_few_tickets: boolean;
  frontpage_event_text_desktop_soldout: boolean;
  frontpage_event_text_desktop_price: boolean;
  frontpage_event_text_mobile_date: boolean;
  frontpage_event_text_mobile_event_name: boolean;
  frontpage_event_text_mobile_venue: boolean;
  frontpage_event_text_mobile_categories: boolean;
  frontpage_event_text_mobile_few_tickets: boolean;
  frontpage_event_text_mobile_soldout: boolean;
  frontpage_event_text_mobile_price: boolean;
  hide_ticketsale_ticket_info: boolean; // Not in use
  hide_ticketsale_venue_info: boolean;
  single_sign_on_exclusive_message: string;
  eventpage_hide_find_tickets_button: boolean;
}

// Utility function to convert API DTO to EventDetail type
export function mapDtoToEventDetail(dto: EventDetailDTO): EventDetail {
  return {
    ...dto,
    categories: dto.categories.map(Number),
    special_categories: dto.special_categories.map(Number),
    saleend_countdown: Boolean(dto.saleend_countdown), 
    show_remaining_tickets: Boolean(dto.show_remaining_tickets), 
    single_sign_on_required: Boolean(dto.single_sign_on_required), 
    remaining_tickets: -1, // Placeholder, as this field is not in the DTO
    element_type: 'event', // Placeholder, as this field is not in the DTO
    promoted: false, // Placeholder, as this field is not in the DTO
    sort_order: 0, // Placeholder, as this field is not in the DTO
    tickets: [], // Placeholder, as this field is not in the DTO
  };
}